import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Save, Settings, Clock, ShieldCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const [callingNumber, setCallingNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [orderStartTime, setOrderStartTime] = useState("");
  const [orderEndTime, setOrderEndTime] = useState("");
  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState("");
  const [whatsappAccessToken, setWhatsappAccessToken] = useState("");
  const [whatsappTemplateName, setWhatsappTemplateName] = useState("");
  const [whatsappMonthlyTemplateName, setWhatsappMonthlyTemplateName] = useState("");
  const [whatsappNoOrdersTemplateName, setWhatsappNoOrdersTemplateName] = useState("");
  const [whatsappMarketingOneTemplateName, setWhatsappMarketingOneTemplateName] = useState("");
  const [whatsappMarketingTwoTemplateName, setWhatsappMarketingTwoTemplateName] = useState("");
  const [showCriticalData, setShowCriticalData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [savingTiming, setSavingTiming] = useState(false);
  const [savingWhatsappApi, setSavingWhatsappApi] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` },
  });

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/settings`, authHeaders());
      setCallingNumber(res.data.calling_number || "");
      setWhatsappNumber(res.data.whatsapp_number || "");
      setOrderStartTime(res.data.order_start_time || "");
      setOrderEndTime(res.data.order_end_time || "");
      setWhatsappPhoneNumberId(res.data.whatsapp_phone_number_id || "");
      setWhatsappAccessToken(res.data.whatsapp_access_token || "");
      setWhatsappTemplateName(res.data.whatsapp_template_name || "");
      setWhatsappMonthlyTemplateName(res.data.whatsapp_monthly_template_name || "");
      setWhatsappNoOrdersTemplateName(res.data.whatsapp_no_orders_template_name || "");
      setWhatsappMarketingOneTemplateName(res.data.whatsapp_marketing_one_template_name || "");
      setWhatsappMarketingTwoTemplateName(res.data.whatsapp_marketing_two_template_name || "");
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        { calling_number: callingNumber, whatsapp_number: whatsappNumber },
        authHeaders()
      );
      toast.success("Contact numbers saved!");
    } catch {
      toast.error("Failed to save contact numbers");
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveTiming = async () => {
    setSavingTiming(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        { order_start_time: orderStartTime, order_end_time: orderEndTime },
        authHeaders()
      );
      toast.success("Order timing saved!");
    } catch {
      toast.error("Failed to save order timing");
    } finally {
      setSavingTiming(false);
    }
  };

  const handleSaveWhatsappApi = async () => {
    if (!whatsappPhoneNumberId.trim() || !whatsappAccessToken.trim() || !whatsappTemplateName.trim()) {
      toast.error("Phone Number ID, Access Token, and Template Name are required");
      return;
    }
    setSavingWhatsappApi(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        {
          whatsapp_phone_number_id: whatsappPhoneNumberId.trim(),
          whatsapp_access_token: whatsappAccessToken.trim(),
          whatsapp_template_name: whatsappTemplateName.trim(),
          whatsapp_monthly_template_name: whatsappMonthlyTemplateName.trim(),
          whatsapp_no_orders_template_name: whatsappNoOrdersTemplateName.trim(),
          whatsapp_marketing_one_template_name: whatsappMarketingOneTemplateName.trim(),
          whatsapp_marketing_two_template_name: whatsappMarketingTwoTemplateName.trim(),
        },
        authHeaders()
      );
      setWhatsappPhoneNumberId("***");
      setWhatsappAccessToken("***");
      toast.success("WhatsApp API details saved!");
    } catch {
      toast.error("Failed to save WhatsApp API details");
    } finally {
      setSavingWhatsappApi(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/export-data`, authHeaders());
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      link.href = downloadUrl;
      link.download = `gasman-data-export-${date}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success("Data export downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExportingData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A4CAE]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#e8eef9] rounded-lg">
            <Settings size={22} className="text-[#0A4CAE]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage your app configuration</p>
          </div>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Contact Numbers */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Phone size={16} className="text-[#0A4CAE]" />
              Contact Numbers
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Shown to customers in the app.</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="calling_number" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Phone size={14} className="text-[#0A4CAE]" />
                Calling Number
              </Label>
              <Input
                id="calling_number"
                type="tel"
                placeholder="e.g. +91 98765 43210"
                value={callingNumber}
                onChange={(e) => setCallingNumber(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="whatsapp_number" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MessageCircle size={14} className="text-green-600" />
                WhatsApp Number
              </Label>
              <Input
                id="whatsapp_number"
                type="tel"
                placeholder="e.g. +91 98765 43210"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="h-10"
              />
            </div>
          </div>
          <div className="px-6 pb-6">
            <Button onClick={handleSaveContact} disabled={savingContact} className="bg-[#0A4CAE] hover:bg-[#083d8a] text-white gap-2">
              <Save size={15} />
              {savingContact ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* Order Timing */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Clock size={16} className="text-[#F7851E]" />
              Order Timing
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Set daily window during which orders are accepted.</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="order_start_time" className="text-sm font-medium text-gray-700">
                Start Time
              </Label>
              <Input
                id="order_start_time"
                type="time"
                value={orderStartTime}
                onChange={(e) => setOrderStartTime(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="order_end_time" className="text-sm font-medium text-gray-700">
                End Time
              </Label>
              <Input
                id="order_end_time"
                type="time"
                value={orderEndTime}
                onChange={(e) => setOrderEndTime(e.target.value)}
                className="h-10"
              />
            </div>
            {orderStartTime && orderEndTime && (
              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                Orders accepted from <span className="font-semibold text-gray-600">{orderStartTime}</span> to <span className="font-semibold text-gray-600">{orderEndTime}</span> daily.
              </p>
            )}
          </div>
          <div className="px-6 pb-6">
            <Button onClick={handleSaveTiming} disabled={savingTiming} className="bg-[#F7851E] hover:bg-[#e0741c] text-white gap-2">
              <Save size={15} />
              {savingTiming ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* WhatsApp API Credentials */}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm lg:col-span-2">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <ShieldCheck size={17} className="text-red-600" />
                WhatsApp API Configuration
              </h2>
              <p className="text-sm text-gray-500 mt-1">Credentials and approved templates used for WhatsApp messages.</p>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="show-critical-data" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Show critical data
              </Label>
              <Switch
                id="show-critical-data"
                checked={showCriticalData}
                onCheckedChange={setShowCriticalData}
                className="data-[state=checked]:bg-red-600"
              />
            </div>
          </div>

          {showCriticalData && (
            <div className="border-t border-red-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_phone_number_id" className="text-sm font-medium text-gray-700">
                    Phone Number ID
                  </Label>
                  <Input
                    id="whatsapp_phone_number_id"
                    value={whatsappPhoneNumberId}
                    onChange={(event) => setWhatsappPhoneNumberId(event.target.value)}
                    placeholder="e.g. 937349779458170"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_access_token" className="text-sm font-medium text-gray-700">
                    Access Token
                  </Label>
                  <Input
                    id="whatsapp_access_token"
                    value={whatsappAccessToken}
                    onChange={(event) => setWhatsappAccessToken(event.target.value)}
                    placeholder="Enter the Meta WhatsApp access token"
                    autoComplete="new-password"
                    className="font-mono text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_template_name" className="text-sm font-medium text-gray-700">
                    OTP Template Name
                  </Label>
                  <Input
                    id="whatsapp_template_name"
                    value={whatsappTemplateName}
                    onChange={(event) => setWhatsappTemplateName(event.target.value)}
                    placeholder="e.g. gasman_wa_otp_template"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_monthly_template_name" className="text-sm font-medium text-gray-700">
                    Monthly Template Name
                  </Label>
                  <Input
                    id="whatsapp_monthly_template_name"
                    value={whatsappMonthlyTemplateName}
                    onChange={(event) => setWhatsappMonthlyTemplateName(event.target.value)}
                    placeholder="Enter the monthly reminder template name"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_no_orders_template_name" className="text-sm font-medium text-gray-700">
                    No Orders Template Name
                  </Label>
                  <Input
                    id="whatsapp_no_orders_template_name"
                    value={whatsappNoOrdersTemplateName}
                    onChange={(event) => setWhatsappNoOrdersTemplateName(event.target.value)}
                    placeholder="Enter the no-orders template name"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_marketing_one_template_name" className="text-sm font-medium text-gray-700">
                    Marketing One Template Name
                  </Label>
                  <Input
                    id="whatsapp_marketing_one_template_name"
                    value={whatsappMarketingOneTemplateName}
                    onChange={(event) => setWhatsappMarketingOneTemplateName(event.target.value)}
                    placeholder="Enter the first marketing template name"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_marketing_two_template_name" className="text-sm font-medium text-gray-700">
                    Marketing Two Template Name
                  </Label>
                  <Input
                    id="whatsapp_marketing_two_template_name"
                    value={whatsappMarketingTwoTemplateName}
                    onChange={(event) => setWhatsappMarketingTwoTemplateName(event.target.value)}
                    placeholder="Enter the second marketing template name"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between gap-4">
                <p className="text-xs text-red-600">Only reveal this section when you need to view or update these credentials.</p>
                <Button
                  onClick={handleSaveWhatsappApi}
                  disabled={savingWhatsappApi}
                  className="bg-red-600 hover:bg-red-700 text-white gap-2 shrink-0"
                >
                  <Save size={15} />
                  {savingWhatsappApi ? "Saving…" : "Save Critical Data"}
                </Button>
              </div>
              <div className="mt-6 border-t border-red-100 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Export Business Data</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Download master data, stores, orders, users, addresses, carts, and delivery managers as JSON. Credentials and tokens are excluded.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExportData}
                  disabled={exportingData}
                  className="border-[#0A4CAE] text-[#0A4CAE] hover:bg-[#e8eef9] gap-2 shrink-0"
                >
                  <Download size={15} />
                  {exportingData ? "Exporting…" : "Download JSON"}
                </Button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;
